'use client';

import { MicVocal } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export function HoldToSpeakButton({
  disabled,
  active,
  label,
  activeLabel,
  onStart,
  onEnd,
}: {
  disabled: boolean;
  active: boolean;
  label: string;
  activeLabel: string;
  onStart: () => void;
  onEnd: () => void;
}) {
  const pointerIdRef = useRef<number | null>(null);
  const keyRef = useRef<string | null>(null);
  const onEndRef = useRef(onEnd);

  useEffect(() => {
    onEndRef.current = onEnd;
  }, [onEnd]);

  const finish = useCallback(() => {
    if (pointerIdRef.current === null && keyRef.current === null) return;
    pointerIdRef.current = null;
    keyRef.current = null;
    onEndRef.current();
  }, []);

  useEffect(() => {
    if (disabled) finish();
  }, [disabled, finish]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') finish();
    };
    window.addEventListener('blur', finish);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('blur', finish);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      finish();
    };
  }, [finish]);

  const onPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (
      disabled ||
      event.button !== 0 ||
      pointerIdRef.current !== null ||
      keyRef.current !== null
    )
      return;
    pointerIdRef.current = event.pointerId;
    event.currentTarget.setPointerCapture(event.pointerId);
    onStart();
  };
  const onPointerUp = (event: PointerEvent<HTMLButtonElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    finish();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (
      disabled ||
      event.repeat ||
      (event.key !== ' ' && event.key !== 'Enter')
    )
      return;
    event.preventDefault();
    if (pointerIdRef.current !== null || keyRef.current !== null) return;
    keyRef.current = event.key;
    onStart();
  };
  const onKeyUp = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (keyRef.current !== event.key) return;
    event.preventDefault();
    finish();
  };

  return (
    <Button
      type="button"
      data-slot="floating-voice-press"
      size="icon"
      variant={active ? 'default' : 'ghost'}
      className={cn(
        'size-8 touch-none rounded-full text-emerald-700 transition-[background-color,box-shadow,transform] duration-150 hover:bg-emerald-500/10 active:scale-[0.96] motion-reduce:transition-none max-md:size-11 dark:text-emerald-300',
        active &&
          'bg-emerald-600 text-white ring-4 ring-emerald-500/25 hover:bg-emerald-600'
      )}
      disabled={disabled}
      aria-label={active ? activeLabel : label}
      aria-pressed={active}
      title={active ? activeLabel : label}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onLostPointerCapture={finish}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onBlur={finish}
    >
      <MicVocal className="size-3.5" aria-hidden />
    </Button>
  );
}
